import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { policiesApi } from '@/lib/supabase';
import { Loader2, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Markdown components defined outside the render function
const MarkdownH2 = ({ children }) => (
  <h2 className="heading-medium mt-12 mb-4 first:mt-0">{children}</h2>
);

const MarkdownH3 = ({ children }) => (
  <h3 className="font-sans text-lg font-medium mt-8 mb-3">{children}</h3>
);

const MarkdownP = ({ children }) => (
  <p className="body-small text-[#1C1C1C] mb-4 leading-relaxed">{children}</p>
);

const MarkdownUl = ({ children }) => (
  <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>
);

const MarkdownLi = ({ children }) => (
  <li className="body-small text-[#1C1C1C]">{children}</li>
);

const MarkdownStrong = ({ children }) => (
  <strong className="font-medium">{children}</strong>
);

const MarkdownA = ({ href, children }) => (
  <a 
    href={href} 
    className="text-[#8B6F47] hover:underline"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

const markdownComponents = {
  h2: MarkdownH2,
  h3: MarkdownH3,
  p: MarkdownP,
  ul: MarkdownUl,
  li: MarkdownLi,
  strong: MarkdownStrong,
  a: MarkdownA,
};

const PolicyPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace('/', ''); // Get slug from path (e.g., /privacy-policy -> privacy-policy)
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const data = await policiesApi.getBySlug(slug);
        if (!data || !data.published) {
          setError('Policy not found');
        } else {
          setPolicy(data);
        }
      } catch (err) {
        console.error('Error fetching policy:', err);
        setError('Failed to load policy');
      } finally {
        setLoading(false);
      }
    };
    if (slug) {
      fetchPolicy();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !policy) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6">
        <h1 className="heading-large mb-4">Page Not Found</h1>
        <p className="body-small text-muted-foreground mb-8">
          The policy page you&apos;re looking for doesn&apos;t exist.
        </p>
        <Link to="/" className="caption link-subtle flex items-center gap-2">
          <ArrowLeft size={14} />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{policy.title} | Curatorial Research</title>
        <meta name="description" content={policy.meta_description} />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#FDFBF7]" data-testid="policy-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 caption text-muted-foreground hover:text-[#1C1C1C] transition-colors mb-12"
          >
            <ArrowLeft size={14} />
            Back to Home
          </Link>

          <header className="mb-12 border-b border-[#E5E0D6] pb-8">
            <h1 className="heading-large mb-4">{policy.title}</h1>
            {policy.last_updated && (
              <p className="caption text-muted-foreground">
                Last Updated: {policy.last_updated}
              </p>
            )}
          </header>

          <article className="prose prose-lg max-w-none">
            <ReactMarkdown components={markdownComponents}>
              {policy.content}
            </ReactMarkdown>
          </article>
        </motion.div>
      </div>
    </>
  );
};

export default PolicyPage;
